/**
 * MyNotesScreen — Sprint 3: Persistent notes from AppContext
 * Lists all notes (manual + AI-saved) or edits a single note.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ArrowLeft, Save, Trash2, Share2, Bold, Italic, List,
  Plus, Sparkles, FileText, Zap, Users, BookOpen, Clock, Download,
} from 'lucide-react';
import { notify } from '../utils/notifications';
import { useApp, type SavedNote } from '../context/AppContext';
import { shareText, exportFile } from '../utils/exportShare';
import { noteToMarkdown } from '../utils/exporters';
import { applyNoteFormat, type NoteFormat } from '../utils/noteFormatting';

// ─── Source icon/label mapping ─────────────────────────────────────────────

function SourceBadge({ source, sourceLabel }: { source: SavedNote['source']; sourceLabel?: string }) {
  const config: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
    manual: { icon: <FileText className="w-3 h-3" />, label: 'Manual Note', color: 'var(--app-text-muted)' },
    'ai-report': { icon: <Sparkles className="w-3 h-3" />, label: 'AI Report', color: 'var(--app-accent)' },
    'ai-session-prep': { icon: <Zap className="w-3 h-3" />, label: 'AI Session Prep', color: 'var(--app-accent)' },
    'ai-paper-insight': { icon: <BookOpen className="w-3 h-3" />, label: 'AI Paper Insight', color: 'var(--app-accent)' },
    'ai-networking': { icon: <Users className="w-3 h-3" />, label: 'AI Networking', color: 'var(--app-accent)' },
    'ai-copilot': { icon: <Sparkles className="w-3 h-3" />, label: 'AI Copilot', color: 'var(--app-accent)' },
  };
  const c = config[source] || config.manual;
  return (
    <span className="flex items-center gap-1 text-[10px]" style={{ color: c.color }}>
      {c.icon}
      {sourceLabel || c.label}
    </span>
  );
}

// ─── Notes List View ─────────────────────────────────────────────────────────

function NotesListView() {
  const navigate = useNavigate();
  const { notes, addNote, authSession, isAuthSessionCurrent } = useApp();

  const handleNewNote = () => {
    const session = authSession;
    if (!session || !isAuthSessionCurrent(session)) return;
    const newNote = addNote({
      title: '',
      content: '',
      source: 'manual',
    }, session);
    if (newNote) navigate(`/notes/${newNote.id}`);
  };

  const handleExportAll = async () => {
    if (notes.length === 0) {
      notify.info('No notes to export');
      return;
    }
    const md = notes.map(noteToMarkdown).join('\n---\n\n');
    try {
      await exportFile({
        filename: `notes-${new Date().toISOString().slice(0, 10)}.md`,
        content: md,
        mimeType: 'text/markdown',
        dialogTitle: 'Export notes',
      });
      notify.success(`Exported ${notes.length} note${notes.length === 1 ? '' : 's'}`);
    } catch {
      notify.error('Could not export notes');
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const getPreview = (content: string) => {
    const lines = content.split('\n').filter(l => l.trim() && !l.startsWith('#') && !l.startsWith('---'));
    return lines.slice(0, 2).join(' ').replace(/[*_#•]/g, '').slice(0, 120) || 'Empty note';
  };

  return (
    <div className="size-full flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>
      {/* Header */}
      <div
        className="app-screen-header flex items-center justify-between px-4 pt-12 pb-4 shrink-0 border-b"
        style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)' }}
      >
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ background: 'var(--app-surface)' }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
        </button>
        <h1 className="text-lg" style={{ color: 'var(--app-text)' }}>My Notes</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportAll}
            aria-label="Export all notes"
            className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-surface)' }}
          >
            <Download className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
          </button>
          <button
            onClick={handleNewNote}
            className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-accent-solid)' }}
          >
            <Plus className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto px-4 py-4 pb-8">
        {notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div
              className="size-16 rounded-full flex items-center justify-center"
              style={{ background: 'var(--app-tag-bg)' }}
            >
              <FileText className="w-8 h-8" style={{ color: 'var(--app-accent)' }} />
            </div>
            <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>No notes yet</p>
            <button
              onClick={handleNewNote}
              className="px-4 py-2 rounded-xl text-sm active:scale-95 transition-transform"
              style={{ background: 'var(--app-accent-solid)', color: '#fff' }}
            >
              Create your first note
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {/* AI-generated notes section */}
            {notes.some(n => n.source !== 'manual') && (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--app-accent)' }} />
                  <p className="text-xs" style={{ color: 'var(--app-accent)' }}>AI-Generated</p>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--app-tag-bg)', color: 'var(--app-accent)' }}>
                    {notes.filter(n => n.source !== 'manual').length}
                  </span>
                </div>
                {notes.filter(n => n.source !== 'manual').map(note => (
                  <button
                    key={note.id}
                    onClick={() => navigate(`/notes/${note.id}`)}
                    className="rounded-xl p-4 border text-left active:scale-[0.98] transition-transform w-full"
                    style={{
                      background: 'var(--app-surface)',
                      borderColor: 'var(--app-tag-border)',
                      boxShadow: 'var(--app-card-shadow)',
                    }}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm flex-1 truncate" style={{ color: 'var(--app-text)' }}>
                        {note.title || 'Untitled Note'}
                      </p>
                      <SourceBadge source={note.source} sourceLabel={note.sourceLabel} />
                    </div>
                    {note.sessionTitle && (
                      <p className="text-[10px] mb-1" style={{ color: 'var(--app-accent)' }}>{note.sessionTitle}</p>
                    )}
                    {note.paperTitle && (
                      <p className="text-[10px] mb-1" style={{ color: 'var(--app-accent)' }}>{note.paperTitle}</p>
                    )}
                    <p className="text-xs line-clamp-2" style={{ color: 'var(--app-text-muted)' }}>
                      {getPreview(note.content)}
                    </p>
                    <p className="text-[10px] mt-2 flex items-center gap-1" style={{ color: 'var(--app-text-subtle)' }}>
                      <Clock className="w-2.5 h-2.5" />
                      {formatDate(note.updatedAt)}
                    </p>
                  </button>
                ))}
                <div className="h-2" />
              </>
            )}

            {/* Manual notes section */}
            {notes.some(n => n.source === 'manual') && (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-3.5 h-3.5" style={{ color: 'var(--app-text-muted)' }} />
                  <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Manual Notes</p>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--app-muted)', color: 'var(--app-text-muted)' }}>
                    {notes.filter(n => n.source === 'manual').length}
                  </span>
                </div>
                {notes.filter(n => n.source === 'manual').map(note => (
                  <button
                    key={note.id}
                    onClick={() => navigate(`/notes/${note.id}`)}
                    className="rounded-xl p-4 border text-left active:scale-[0.98] transition-transform w-full"
                    style={{
                      background: 'var(--app-surface)',
                      borderColor: 'var(--app-border)',
                      boxShadow: 'var(--app-card-shadow)',
                    }}
                  >
                    <p className="text-sm mb-0.5" style={{ color: 'var(--app-text)' }}>
                      {note.title || 'Untitled Note'}
                    </p>
                    {note.sessionTitle && (
                      <p className="text-[10px] mb-1" style={{ color: 'var(--app-accent)' }}>{note.sessionTitle}</p>
                    )}
                    <p className="text-xs line-clamp-2" style={{ color: 'var(--app-text-muted)' }}>
                      {getPreview(note.content)}
                    </p>
                    <p className="text-[10px] mt-2 flex items-center gap-1" style={{ color: 'var(--app-text-subtle)' }}>
                      <Clock className="w-2.5 h-2.5" />
                      {formatDate(note.updatedAt)}
                    </p>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Note Editor View ─────────────────────────────────────────────────────────

function NoteEditorView({ noteId }: { noteId: string }) {
  const navigate = useNavigate();
  const {
    getNoteById,
    updateNote,
    deleteNote,
    addNote,
    authSession,
    isAuthSessionCurrent,
  } = useApp();

  const existingNote = getNoteById(noteId);

  // If note doesn't exist in context, create a new one
  const [currentNote, setCurrentNote] = useState<SavedNote | null>(existingNote || null);
  const [title, setTitle] = useState(existingNote?.title || '');
  const [content, setContent] = useState(existingNote?.content || '');
  const [hasChanges, setHasChanges] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const session = authSession;
    if (!existingNote && noteId !== 'new' && session && isAuthSessionCurrent(session)) {
      // Try to create a blank note for legacy IDs
      const newNote = addNote({
        title: '',
        content: '',
        source: 'manual',
      }, session);
      setCurrentNote(newNote);
    }
  }, []);

  // Auto-save
  useEffect(() => {
    if (!hasChanges) return;
    const timer = setTimeout(() => {
      const id = currentNote?.id || existingNote?.id;
      const session = authSession;
      if (id && session && isAuthSessionCurrent(session)) {
        updateNote(id, { title, content }, session);
        setAutoSaving(false);
        setHasChanges(false);
      }
    }, 1500);
    setAutoSaving(true);
    return () => clearTimeout(timer);
  }, [hasChanges, title, content]);

  const handleChange = () => setHasChanges(true);

  const handleDelete = () => {
    const session = authSession;
    const id = currentNote?.id || existingNote?.id;
    if (!id || !session || !isAuthSessionCurrent(session)) return;
    deleteNote(id, session);
    notify.success('Note deleted');
    navigate(-1);
  };

  const handleShare = async () => {
    const src = existingNote ?? currentNote;
    const md = noteToMarkdown({
      ...(src as SavedNote),
      title: title || src?.title || 'Untitled Note',
      content,
    } as SavedNote);
    try {
      const ok = await shareText({ title: title || 'Note', text: md, dialogTitle: 'Share note' });
      if (ok) notify.success('Note shared');
      else notify.info('Sharing cancelled');
    } catch {
      notify.error('Could not share note');
    }
  };

  const insertFormat = (format: NoteFormat) => {
    const textarea = contentRef.current;
    if (!textarea) return;

    const formatted = applyNoteFormat(content, textarea.selectionStart, textarea.selectionEnd, format);
    setContent(formatted.content);
    handleChange();

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(formatted.selectionStart, formatted.selectionEnd);
    });
  };

  const note = existingNote || currentNote;
  const isAINote = note && note.source !== 'manual';

  return (
    <div className="size-full flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>
      {/* Header */}
      <div
        className="app-screen-header flex items-center justify-between px-4 pt-12 pb-4 shrink-0 border-b"
        style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)' }}
      >
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ background: 'var(--app-surface)' }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
        </button>

        <div className="flex items-center gap-1.5">
          {!hasChanges && !autoSaving && (
            <span className="text-xs flex items-center gap-1" style={{ color: 'var(--app-text-muted)' }}>
              <Save className="w-3 h-3" />
              Saved
            </span>
          )}
          {autoSaving && (
            <span className="text-[#d97706] text-xs">Saving...</span>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleShare}
            className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-surface)' }}
          >
            <Share2 className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
          </button>
          <button
            onClick={handleDelete}
            className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-surface)' }}
          >
            <Trash2 className="w-4 h-4 text-[#ef4444]" />
          </button>
        </div>
      </div>

      {/* AI source badge */}
      {isAINote && note && (
        <div className="px-4 pt-3">
          <SourceBadge source={note.source} sourceLabel={note.sourceLabel} />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-1 px-4 py-2 border-b shrink-0" style={{ borderColor: 'var(--app-border)' }}>
        <button
          aria-label="Bold selected note text"
          title="Bold"
          onPointerDown={event => event.preventDefault()}
          onClick={() => insertFormat('bold')}
          className="size-8 rounded flex items-center justify-center transition-colors active:scale-90"
          style={{ color: 'var(--app-text-subtle)' }}
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          aria-label="Italicize selected note text"
          title="Italic"
          onPointerDown={event => event.preventDefault()}
          onClick={() => insertFormat('italic')}
          className="size-8 rounded flex items-center justify-center transition-colors active:scale-90"
          style={{ color: 'var(--app-text-subtle)' }}
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          aria-label="Turn selected note text into a list"
          title="List"
          onPointerDown={event => event.preventDefault()}
          onClick={() => insertFormat('list')}
          className="size-8 rounded flex items-center justify-center transition-colors active:scale-90"
          style={{ color: 'var(--app-text-subtle)' }}
        >
          <List className="w-4 h-4" />
        </button>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* Title */}
        <div className="px-4 pt-4">
          <input
            type="text"
            value={title}
            onChange={e => { setTitle(e.target.value); handleChange(); }}
            placeholder="Note title..."
            className="w-full bg-transparent text-2xl outline-none tracking-tight"
            style={{ color: 'var(--app-text)' }}
          />
        </div>

        {/* Session/Paper tag */}
        {note?.sessionTitle && (
          <div className="px-4 mt-2">
            <span className="text-xs" style={{ color: 'var(--app-accent)' }}>{note.sessionTitle}</span>
          </div>
        )}
        {note?.paperTitle && (
          <div className="px-4 mt-2">
            <span className="text-xs" style={{ color: 'var(--app-accent)' }}>{note.paperTitle}</span>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 px-4 pt-3 pb-8">
          <textarea
            ref={contentRef}
            value={content}
            onChange={e => { setContent(e.target.value); handleChange(); }}
            placeholder="Start writing your notes..."
            className="w-full h-full min-h-[400px] bg-transparent text-sm leading-relaxed outline-none resize-none"
            style={{ color: 'var(--app-text)', caretColor: 'var(--app-accent)' }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Main Screen (router) ─────────────────────────────────────────────────────

export default function MyNotesScreen() {
  const { id } = useParams();

  // If no ID, show list view; otherwise show editor
  if (!id) {
    return <NotesListView />;
  }

  return <NoteEditorView noteId={id} />;
}
